import React, { useState, useEffect } from 'react';
import {
    Shield,
    Globe,
    Download,
    Zap,
    DollarSign,
    QrCode,
    ExternalLink,
    Apple,
    Sparkles,
    Target,
    User
} from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';



const AppShowcase: React.FC = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentScreen, setCurrentScreen] = useState(0);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    useEffect(() => {
        const screenInterval = setInterval(() => {
            setCurrentScreen((prev) => (prev + 1) % 4);
        }, 4000);
        return () => clearInterval(screenInterval);
    }, []);

    const premiumFeatures = [
        {
            icon: Shield,
            title: "Bank-Grade Security",
            description: "256-bit encryption with biometric authentication",
            color: "from-green-500 to-emerald-600"
        },
        {
            icon: Zap,
            title: "Real-Time Updates",
            description: "Instant notifications and live market data",
            color: "from-blue-500 to-cyan-600"
        },
        {
            icon: Target,
            title: "Smart Investment",
            description: "AI-powered recommendations and auto-investment",
            color: "from-purple-500 to-violet-600"
        },
        {
            icon: Globe,
            title: "Global Access",
            description: "Invest from anywhere with 24/7 platform access",
            color: "from-orange-500 to-amber-600"
        }
    ];

    const installationSteps = [
        {
            step: "01",
            title: "Download App",
            description: "Get Diasporawise from App Store or Google Play",
            icon: Download
        },
        {
            step: "02",
            title: "Create Account",
            description: "Sign up with email and verify your identity",
            icon: User
        },
        {
            step: "03",
            title: "Complete KYC",
            description: "Upload documents for secure verification",
            icon: Shield
        },
        {
            step: "04",
            title: "Start Investing",
            description: "Browse projects and make your first investment",
            icon: DollarSign
        }
    ];



    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black">
            {/* Navbar */}
            <Navbar />

            {/* Hero Section - Premium Design */}
            <section className="relative overflow-hidden py-20 px-4">
                {/* Animated Background */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-20 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute top-40 right-20 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
                    <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-blue-400/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }}></div>
                </div>

                <div className="container mx-auto max-w-7xl relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        {/* Left Section - Premium Content */}
                        <div className="text-white">
                            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                                {/* Premium Badge */}
                                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                                    <Sparkles className="w-4 h-4" />
                                    Premium Investment Platform
                                </div>

                                <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                                    Diasporawise
                                    <span className="block bg-gradient-to-r from-blue-400 to-blue-300 bg-clip-text text-transparent">
                                        Investment App
                                    </span>
                                </h1>

                                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                    Experience the future of real estate investment with our premium mobile application.
                                    Secure, transparent, and profitable opportunities await at your fingertips.
                                </p>



                                {/* Download Buttons */}
                                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                                    <a
                                        href="https://play.google.com/store/apps/details?id=com.realestateinvest.app"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group bg-white text-black px-8 py-4 rounded-2xl font-semibold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4"
                                    >
                                        <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
                                            <div className="w-4 h-4 bg-white rounded-sm"></div>
                                        </div>
                                        <div className="text-left">
                                            <div className="text-xs font-medium">GET IT ON</div>
                                            <div className="text-lg font-bold">Google Play</div>
                                        </div>
                                        <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                                    </a>

                                    <a
                                        href="https://app.diasporawise.com"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group bg-white text-black px-8 py-4 rounded-2xl font-semibold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4"
                                    >
                                        <Apple className="w-8 h-8" />
                                        <div className="text-left w-full sm:w-auto">
                                            <div className="text-[10px] sm:text-xs font-medium">Download on the</div>
                                            <div className="text-base sm:text-lg font-bold">App Store</div>
                                        </div>
                                        <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300 hidden sm:block" />
                                    </a>
                                </div>

                                {/* QR Code Section */}
                                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/10 w-full sm:w-auto">
                                    <h3 className="text-base sm:text-lg font-semibold text-white mb-4 flex items-center gap-2 justify-center sm:justify-start">
                                        <QrCode className="w-5 h-5" />
                                        Scan QR Code to Download
                                    </h3>
                                    <div className="flex flex-col sm:flex-row items-center gap-6">
                                        <div className="w-48 h-48 sm:w-56 sm:h-56 bg-white rounded-xl flex items-center justify-center shadow-lg overflow-hidden p-2">
                                            <img
                                                src="/diasporwise qr play store.jpg"
                                                alt="QR Code for Download"
                                                className="w-full h-full object-contain"
                                            />
                                        </div>
                                        <div className="text-center sm:text-left text-white/90">
                                            <p className="font-medium mb-1 text-sm sm:text-base">Point your camera at the QR code</p>
                                            <p className="text-xs sm:text-sm text-gray-400">Available for Android & iOS</p>
                                            <div className="flex gap-2 mt-4 sm:mt-2 justify-center sm:justify-start">
                                                <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Section - Premium Phone Mockup */}
                        <div className="relative flex justify-center lg:justify-end">
                            <div className="relative">
                                <img
                                    src="/app_image-removebg-preview.png"
                                    alt="Diasporawise Mobile App"
                                    className="w-full max-w-[400px] h-auto rounded-2xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Premium Features Section */}
            <section className="py-20 px-4">
                <div className="container mx-auto max-w-7xl">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 px-4 py-2 rounded-full text-sm font-semibold text-white mb-6">
                            <Sparkles className="w-4 h-4" />
                            Premium Features
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                            Why Choose Diasporawise?
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            Experience the most advanced real estate investment platform with cutting-edge features and security.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {premiumFeatures.map((feature, index) => {
                            const IconComponent = feature.icon;
                            return (
                                <div
                                    key={index}
                                    className="group bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all duration-300 transform hover:-translate-y-4 cursor-pointer"
                                    style={{ animationDelay: `${index * 150}ms` }}
                                >
                                    <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                                        <IconComponent className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-4 group-hover:text-blue-400 transition-colors duration-300">
                                        {feature.title}
                                    </h3>
                                    <p className="text-gray-400 leading-relaxed">
                                        {feature.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Installation Process */}
            <section className="py-20 px-4 bg-gradient-to-r from-gray-800 to-gray-900">
                <div className="container mx-auto max-w-7xl">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                            Get Started in 4 Simple Steps
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            Join thousands of investors who trust Diasporawise for their real estate investments.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {installationSteps.map((step, index) => {
                            const IconComponent = step.icon;
                            return (
                                <div
                                    key={index}
                                    className="relative group"
                                    style={{ animationDelay: `${index * 200}ms` }}
                                >
                                    {/* Connection Line */}
                                    {index < installationSteps.length - 1 && (
                                        <div className="hidden lg:block absolute top-1/2 left-full w-full h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 transform -translate-y-1/2 z-0"></div>
                                    )}

                                    <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-blue-500/50 transition-all duration-300 transform hover:-translate-y-2 group-hover:shadow-2xl group-hover:shadow-blue-500/25">
                                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                            <IconComponent className="w-8 h-8 text-white" />
                                        </div>
                                        <div className="absolute top-4 right-4 w-8 h-8 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                            {step.step}
                                        </div>
                                        <h3 className="text-xl font-bold text-white mb-4 group-hover:text-blue-400 transition-colors duration-300">
                                            {step.title}
                                        </h3>
                                        <p className="text-gray-400 leading-relaxed">
                                            {step.description}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* App Screenshots Gallery */}
            <section className="py-20 px-4">
                <div className="container mx-auto max-w-7xl">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                            App Screenshots
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            Explore the beautiful and intuitive interface of our premium investment platform.
                        </p>
                    </div>

                    <div className="flex justify-center items-center space-x-8 overflow-x-auto pb-8">
                        {[
                            { image: "/image1.jpg", alt: "Home Dashboard" },
                            { image: "/image6.jpg", alt: "Project Explorer" },
                            { image: "/image2.jpg", alt: "Investment Hub" },
                            { image: "/image18.jpeg", alt: "Analytics Center" }
                        ].map((screenshot, index) => (
                            <div
                                key={index}
                                className={`relative flex-shrink-0 transition-all duration-500 transform ${currentScreen === index ? 'scale-110' : 'scale-100'
                                    }`}
                            >
                                <div className="w-64 h-[500px] rounded-[2rem] shadow-2xl border-8 border-gray-700 overflow-hidden">
                                    <img
                                        src={screenshot.image}
                                        alt={screenshot.alt}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="py-20 px-4 bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600">
                <div className="container mx-auto max-w-4xl text-center">
                    <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-semibold text-white mb-6">
                        <Sparkles className="w-4 h-4" />
                        Ready to Start Investing?
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Download Diasporawise Today
                    </h2>
                    <p className="text-xl text-white/90 mb-8">
                        Join thousands of investors who trust Diasporawise for their real estate investments.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                        <a
                            href="https://play.google.com/store/apps/details?id=com.realestateinvest.app"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group bg-white text-black px-8 py-4 rounded-2xl font-semibold shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4"
                        >
                            <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
                                <div className="w-4 h-4 bg-white rounded-sm"></div>
                            </div>
                            <div className="text-left">
                                <div className="text-xs font-medium">GET IT ON</div>
                                <div className="text-lg font-bold">Google Play</div>
                            </div>
                            <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </a>

                        <a
                            href="https://app.diasporawise.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group bg-white text-black px-8 py-4 rounded-2xl font-semibold shadow-2xl hover:shadow-pink-500/25 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4"
                        >
                            <Apple className="w-8 h-8" />
                            <div className="text-left">
                                <div className="text-xs font-medium">Download on the</div>
                                <div className="text-lg font-bold">App Store</div>
                            </div>
                            <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </a>
                    </div>

                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 inline-block">
                        <h3 className="text-lg font-semibold text-white mb-4">Scan QR Code</h3>
                        <div className="w-56 h-56 bg-white rounded-xl flex items-center justify-center mx-auto shadow-lg overflow-hidden p-2">
                            <img
                                src="/diasporwise qr play store.jpg"
                                alt="QR Code for Download"
                                className="w-full h-full object-contain"
                            />
                        </div>
                        <p className="text-white/80 text-sm mt-2">Available for Android & iOS</p>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default AppShowcase; 